import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { listAgreementVersions } from '../services/agreementService';
import type { AgreementVersionInfo } from '../types';
import { Button } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Modal } from '../components/ui/Modal';

function formatDate(dateStr: string): string {
  try {
    const date = new Date(dateStr);
    // Add 5 hours 30 minutes for IST (UTC+5:30)
    const istDate = new Date(date.getTime() + (5.5 * 60 * 60 * 1000));
    return istDate.toLocaleString('en-IN', { 
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  } catch {
    return dateStr;
  }
}

function isFormulaChanged(
  current: AgreementVersionInfo,
  previous: AgreementVersionInfo | null,
  field: keyof AgreementVersionInfo
): boolean {
  if (!previous) return false;
  return current[field] !== previous[field];
}

export default function AgreementVersionHistoryPage() {
  const { agreementId } = useParams<{ agreementId: string }>();
  const navigate = useNavigate();

  const [versions, setVersions] = useState<AgreementVersionInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVersion, setSelectedVersion] = useState<AgreementVersionInfo | null>(null);

  useEffect(() => {
    if (!agreementId) return;

    async function fetchVersions() {
      setIsLoading(true);
      setError(null);
      try {
        const data = await listAgreementVersions(Number(agreementId));
        setVersions(data);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to load version history';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    }

    fetchVersions();
  }, [agreementId]);

  // Build a map from versionNumber to the previous version for change highlighting
  const previousVersionMap = new Map<number, AgreementVersionInfo>();
  const sorted = [...versions].sort((a, b) => a.versionNumber - b.versionNumber);
  for (let i = 1; i < sorted.length; i++) {
    previousVersionMap.set(sorted[i].versionNumber, sorted[i - 1]);
  }

  return (
    <div className="agreement-version-history-page">
      <div className="page-header">
        <h2>Agreement Version History {agreementId ? `(Agreement #${agreementId})` : ''}</h2>
        <Button variant="secondary" onClick={() => navigate('/agreements')}>
          Back to Agreements
        </Button>
      </div>

      {error && (
        <Alert type="error" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      {isLoading ? (
        <LoadingSpinner message="Loading version history..." />
      ) : versions.length === 0 ? (
        <p className="history-empty">No version history entries found.</p>
      ) : (
        <table className="data-table" role="table">
          <thead>
            <tr>
              <th scope="col">Version</th>
              <th scope="col">Changed By</th>
              <th scope="col">Changed At</th>
              <th scope="col">Description</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((version) => {
              const prev = previousVersionMap.get(version.versionNumber) ?? null;
              const hasFormulaChanges =
                isFormulaChanged(version, prev, 'hospitalShareNlFormula') ||
                isFormulaChanged(version, prev, 'hospitalShareExecutable') ||
                isFormulaChanged(version, prev, 'arApNlFormula') ||
                isFormulaChanged(version, prev, 'arApExecutable');

              return (
                <tr key={version.versionId}>
                  <td>
                    <span className="change-badge change-badge-updated">
                      v{version.versionNumber}
                    </span>
                    {hasFormulaChanges && (
                      <span className="formula-changed-indicator" title="Formula changes detected">
                        {' '}⚠
                      </span>
                    )}
                  </td>
                  <td>{version.changedBy}</td>
                  <td>{formatDate(version.changedAt)}</td>
                  <td>{version.changeDescription || '—'}</td>
                  <td>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setSelectedVersion(version)}
                    >
                      View Details
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <Modal
        isOpen={selectedVersion !== null}
        onClose={() => setSelectedVersion(null)}
        title={`Version ${selectedVersion?.versionNumber ?? ''} Details`}
        size="lg"
      >
        {selectedVersion && (
          <VersionDetailView
            version={selectedVersion}
            previousVersion={previousVersionMap.get(selectedVersion.versionNumber) ?? null}
          />
        )}
      </Modal>
    </div>
  );
}

function VersionDetailView({
  version,
  previousVersion,
}: {
  version: AgreementVersionInfo;
  previousVersion: AgreementVersionInfo | null;
}) {
  const sectionStyle: React.CSSProperties = {
    marginBottom: '1.25rem',
    padding: '1rem',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '0.35rem',
  };

  const valueStyle: React.CSSProperties = {
    fontSize: '0.9rem',
    color: '#1f2937',
  };

  const codeStyle: React.CSSProperties = {
    fontFamily: 'monospace',
    fontSize: '0.82rem',
    backgroundColor: '#f3f4f6',
    padding: '0.6rem 0.75rem',
    borderRadius: '6px',
    border: '1px solid #e5e7eb',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    display: 'block',
    color: '#1f2937',
    lineHeight: 1.5,
  };

  const changedBadge: React.CSSProperties = {
    display: 'inline-block',
    fontSize: '0.65rem',
    fontWeight: 600,
    padding: '0.1rem 0.4rem',
    borderRadius: '9999px',
    backgroundColor: '#fef3c7',
    color: '#92400e',
    marginLeft: '0.5rem',
    verticalAlign: 'middle',
  };

  const prevStyle: React.CSSProperties = {
    marginTop: '0.5rem',
    padding: '0.5rem 0.75rem',
    backgroundColor: '#fef2f2',
    borderRadius: '6px',
    border: '1px dashed #fca5a5',
    fontSize: '0.8rem',
    color: '#991b1b',
  };

  function hasChanged(field: keyof AgreementVersionInfo): boolean {
    if (!previousVersion) return false;
    return version[field] !== previousVersion[field];
  }

  return (
    <div style={{ maxHeight: '70vh', overflowY: 'auto', padding: '0.25rem' }}>
      {/* Meta info row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: '0.75rem',
        marginBottom: '1.25rem',
        padding: '0.75rem 1rem',
        backgroundColor: '#eff6ff',
        borderRadius: '8px',
        border: '1px solid #bfdbfe',
      }}>
        <div>
          <div style={labelStyle}>Version</div>
          <div style={{ ...valueStyle, fontWeight: 700, fontSize: '1.1rem', color: '#1d4ed8' }}>
            v{version.versionNumber}
          </div>
        </div>
        <div>
          <div style={labelStyle}>Changed By</div>
          <div style={valueStyle}>{version.changedBy}</div>
        </div>
        <div>
          <div style={labelStyle}>Changed At</div>
          <div style={valueStyle}>{formatDate(version.changedAt)}</div>
        </div>
        {version.changeDescription && (
          <div style={{ gridColumn: '1 / -1' }}>
            <div style={labelStyle}>Description</div>
            <div style={valueStyle}>{version.changeDescription}</div>
          </div>
        )}
      </div>

      {/* Hospital Share NL Formula */}
      <div style={sectionStyle}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
          <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>
            Hospital Share Formula
          </span>
          {hasChanged('hospitalShareNlFormula') && <span style={changedBadge}>CHANGED</span>}
        </div>
        <div style={codeStyle}>{version.hospitalShareNlFormula || '—'}</div>
        {hasChanged('hospitalShareNlFormula') && previousVersion?.hospitalShareNlFormula && (
          <div style={prevStyle}>
            <span style={{ fontWeight: 600 }}>Previous:</span> {previousVersion.hospitalShareNlFormula}
          </div>
        )}
      </div>

      {/* Hospital Share Executable */}
      <div style={sectionStyle}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
          <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>
            Hospital Share (Executable)
          </span>
          {hasChanged('hospitalShareExecutable') && <span style={changedBadge}>CHANGED</span>}
        </div>
        <code style={codeStyle}>{version.hospitalShareExecutable || '—'}</code>
        {hasChanged('hospitalShareExecutable') && previousVersion?.hospitalShareExecutable && (
          <div style={prevStyle}>
            <span style={{ fontWeight: 600 }}>Previous:</span>{' '}
            <code style={{ fontFamily: 'monospace' }}>{previousVersion.hospitalShareExecutable}</code>
          </div>
        )}
      </div>

      {/* AR/AP NL Formula */}
      <div style={sectionStyle}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
          <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>
            AR/AP Formula
          </span>
          {hasChanged('arApNlFormula') && <span style={changedBadge}>CHANGED</span>}
        </div>
        <div style={codeStyle}>{version.arApNlFormula || '—'}</div>
        {hasChanged('arApNlFormula') && previousVersion?.arApNlFormula && (
          <div style={prevStyle}>
            <span style={{ fontWeight: 600 }}>Previous:</span> {previousVersion.arApNlFormula}
          </div>
        )}
      </div>

      {/* AR/AP Executable */}
      <div style={sectionStyle}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
          <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>
            AR/AP (Executable)
          </span>
          {hasChanged('arApExecutable') && <span style={changedBadge}>CHANGED</span>}
        </div>
        <code style={codeStyle}>{version.arApExecutable || '—'}</code>
        {hasChanged('arApExecutable') && previousVersion?.arApExecutable && (
          <div style={prevStyle}>
            <span style={{ fontWeight: 600 }}>Previous:</span>{' '}
            <code style={{ fontFamily: 'monospace' }}>{previousVersion.arApExecutable}</code>
          </div>
        )}
      </div>

      {/* Parameters Snapshot */}
      {version.parametersSnapshot && Array.isArray(version.parametersSnapshot) && version.parametersSnapshot.length > 0 && (
        <div style={sectionStyle}>
          <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>
            Parameters Snapshot
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: '0.82rem', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: '#6b7280', fontWeight: 600 }}>Name</th>
                  <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: '#6b7280', fontWeight: 600 }}>Type</th>
                  <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: '#6b7280', fontWeight: 600 }}>Formula</th>
                </tr>
              </thead>
              <tbody>
                {(version.parametersSnapshot as Array<Record<string, unknown>>).map((p, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '0.4rem 0.5rem', fontWeight: 500 }}>
                      {String(p.parameter_name || p.parameterName || '—')}
                    </td>
                    <td style={{ padding: '0.4rem 0.5rem' }}>
                      {String(p.parameter_type || p.parameterType || '—')}
                    </td>
                    <td style={{ padding: '0.4rem 0.5rem', fontFamily: 'monospace', fontSize: '0.78rem' }}>
                      {String(p.natural_language_formula || p.naturalLanguageFormula || p.executable_formula || p.executableFormula || '—')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Raw JSON fallback for non-array snapshots */}
      {version.parametersSnapshot && !Array.isArray(version.parametersSnapshot) && (
        <div style={sectionStyle}>
          <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>
            Parameters Snapshot
          </div>
          <pre style={{ ...codeStyle, maxHeight: '200px', overflow: 'auto', fontSize: '0.78rem' }}>
            {JSON.stringify(version.parametersSnapshot, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

